import { useState, useEffect, useRef, Fragment, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../config';
import {
  EM_BRAND_CHINT_API,
  EM_BRAND_CHINT_LABEL,
  energyMeterBrandFromApi,
  energyMeterBrandToApi,
  isEnergyMeterChintProfile,
  buildEnergyMeterDetailPath,
} from '../utils/energyMeterBrandDisplay';

const BACKEND_URL = getBackendUrl();

const getPhotoUrl = (item) => {
  if (item?.photo_url || item?.logo_url) return `${BACKEND_URL}${item.photo_url || item.logo_url}`;
  return 'https://customer-assets.emergentagent.com/job_muj-pytja-start/artifacts/8a2lsskt_IMG-20251218-WA0002.jpg';
};

const normalizeDeviceType = (t) => String(t || '').trim().toUpperCase();

const Devices = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm } = useConfirm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const [selectedDatalogger, setSelectedDatalogger] = useState(null);

  const [dataLoggers, setDataLoggers] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [energyMeters, setEnergyMeters] = useState([]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [addDeviceType, setAddDeviceType] = useState('');
  const [formData, setFormData] = useState({
    brand: '',
    serial_number: '',
    model: '',
    ip_address: '',
    name: '',
    company_id: '',
    data_logger_id: '',
    port: 502,
    unit_id: '',
    power_kw: '',
    serial_path: '',
    edge_api_base_url: '',
    display_power_unit: 'mw',
    chint_kwh_transform: '1',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [deviceView, setDeviceView] = useState('list');
  const [showDetails, setShowDetails] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisPeriod, setAnalysisPeriod] = useState('day');
  const [showEdgePopup, setShowEdgePopup] = useState(false);
  const [edgePopupDevice, setEdgePopupDevice] = useState(null);

  const fileInputRef = useRef(null);
  const photoFileRef = useRef(null); // ref backup so save always sees the selected file
  const longPressTimerRef = useRef(null);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Brendet për dropdown: Dio Logger i pari për data logger; KACO për RTU; Energy Meter veçmas
  const DATA_LOGGER_INVERTER_BRANDS = ['Dio Logger', 'Huawei', 'Sungrow', 'GoodWe', 'Growatt', 'KACO'];
  const ENERGY_METER_BRANDS = ['Schneider', 'Siemens', EM_BRAND_CHINT_LABEL];
  const getBrandOptions = () => {
    if (addDeviceType === 'energymeter') return ENERGY_METER_BRANDS;
    if (addDeviceType === 'datalogger' || addDeviceType === 'inverter') return DATA_LOGGER_INVERTER_BRANDS;
    return [];
  };
  const brandOptions = getBrandOptions();
  const effectiveBrandOptions = (formData.brand && formData.brand.trim() && !brandOptions.includes(formData.brand)) ? [formData.brand.trim(), ...brandOptions] : brandOptions;

  const extractId = (v) => {
    if (!v) return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
    if (typeof v === 'object') {
      // Mongo style { $oid: "..." } ose { oid: "..." } ose { id: "..." }
      return String(v.$oid || v.oid || v.id || '').trim();
    }
    return String(v).trim();
  };

  const selectedCompanyId = extractId(selectedCompany?.id);

  const getCompanyEdgeForForm = useCallback((companyId) => {
    const id = companyId != null ? String(companyId).trim() : '';
    if (!id) return '';
    const co = companies.find((c) => String(c.id) === id);
    return (co && co.edge_api_base_url) ? String(co.edge_api_base_url).trim() : '';
  }, [companies]);

  const updateCompanyInUrl = (companyId) => {
    const params = new URLSearchParams(searchParams);
    const id = companyId != null ? String(companyId).trim() : '';
    if (id && id !== 'undefined' && id !== 'null') params.set('company', id);
    else params.delete('company');
    navigate({ pathname: '/devices', search: params.toString() }, { replace: true });
  };

  const switchCompany = (company) => {
    setSelectedCompany(company || null);
    setShowCompanyDropdown(false);
    setSelectedDatalogger(null);

    // reset form selection so dropdown mos me mbet prej kompanisë tjetër
    setFormData((prev) => ({
      ...prev,
      company_id: extractId(company?.id),
      data_logger_id: ''
    }));

    if (company?.id) updateCompanyInUrl(company.id);
  };

  useEffect(() => {
    fetchData();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const tabParam = searchParams.get('tab');
    if (tabParam === 'inverter') setActiveTab('inverter');
    else if (tabParam === 'datalogger') setActiveTab('datalogger');
    else if (tabParam === 'ems') setActiveTab('ems');

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (!companyParam || companyParam === 'undefined' || companyParam === 'null') return;
    if (companies.length === 0) return;
    const company = companies.find(c => String(c.id) === String(companyParam));
    if (company) setSelectedCompany(company);
  }, [searchParams, companies]);

  useEffect(() => {
    if (activeTab !== 'inverter') setSelectedDatalogger(null);
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const companiesRes = await axios.get(`${BACKEND_URL}/api/companies`, { headers });
      const companiesData = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      setCompanies(companiesData);

      const companyParam = searchParams.get('company');
      const invalidCompanyParam = !companyParam || companyParam === 'undefined' || companyParam === 'null';
      const companyIdFromUrl = invalidCompanyParam ? null : companyParam;
      const selected = companyIdFromUrl
        ? companiesData.find(c => String(c.id) === String(companyIdFromUrl))
        : companiesData[0];

      setSelectedCompany(selected || null);

      const selectedId = selected?.id ? String(selected.id) : '';

      if (selected && invalidCompanyParam) {
        const params = new URLSearchParams(searchParams);
        params.set('company', String(selected.id));
        navigate({ pathname: '/devices', search: params.toString() }, { replace: true });
      }

      if (!selectedId) {
        setDataLoggers([]);
        setInverters([]);
        setEnergyMeters([]);
      } else {
        const [dataLoggersRes, invertersRes, energyMetersRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/devices/data-loggers?company_id=${encodeURIComponent(selectedId)}`, { headers }),
          axios.get(`${BACKEND_URL}/api/devices/inverters?company_id=${encodeURIComponent(selectedId)}`, { headers }),
          axios.get(`${BACKEND_URL}/api/devices/energy-meters?company_id=${encodeURIComponent(selectedId)}`, { headers }),
        ]);
        setDataLoggers(dataLoggersRes.data || []);
        setInverters(invertersRes.data || []);
        setEnergyMeters(energyMetersRes.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };


  const cardShadowClass =
    "shadow-[6px_6px_12px_rgba(0,0,0,0.08),-6px_-6px_12px_rgba(255,255,255,0.9)] dark:shadow-none";

  const getCompanyDataLoggers = () => {
    if (!selectedCompanyId) return [];
    return dataLoggers.filter(dl => extractId(dl.company_id) === selectedCompanyId);
  };
  // ✅ FIX: 'INVERTER' jo 'inverter'
  const getCompanyInverters = () => {
    if (!selectedCompanyId) return [];
    return inverters.filter(inv =>
      extractId(inv.company_id) === selectedCompanyId &&
      normalizeDeviceType(inv.device_type) === 'INVERTER'
    );
  };

  const getCompanyEnergyMeters = () => {
    if (!selectedCompanyId) return [];
    const t = (x) => normalizeDeviceType(x);
    return energyMeters.filter(em =>
      extractId(em.company_id) === selectedCompanyId &&
      (t(em.device_type) === 'ENERGY_METER' || t(em.device_type) === 'ENERGYMETER')
    );
  };

  const getDataloggerNameForInverter = (inverter) => {
    if (!inverter) return '';
    const dlId = extractId(inverter.data_logger_id);
    if (!dlId) return '';
    const dl = getCompanyDataLoggers().find((d) => String(extractId(d.id)) === String(dlId));
    return (dl?.name && String(dl.name).trim()) || '';
  };

  const getAllDevices = () => [
    ...getCompanyDataLoggers().map(d => ({ ...d, type: 'datalogger' })),
    ...getCompanyInverters().map(d => ({ ...d, type: 'inverter' })),
    ...getCompanyEnergyMeters().map(d => ({ ...d, type: 'energymeter' }))
  ];

  const getFilteredDevices = () => {
    if (activeTab === 'all') return getAllDevices();
    if (activeTab === 'inverter') return getCompanyInverters().map(d => ({ ...d, type: 'inverter' }));
    if (activeTab === 'datalogger') return getCompanyDataLoggers().map(d => ({ ...d, type: 'datalogger' }));
    if (activeTab === 'ems') {
      return getCompanyEnergyMeters().map(d => ({ ...d, type: 'energymeter' }));
    }
    return [];
  };

  // Status nga collections reader – datalogger: plant_status_text, inverter: device_status_text (join në backend)
  const _getStatusLabelForStyle = (device) => {
    const plantText = device?.plant_status_text ?? device?.plantStatusText;
    const deviceText = device?.device_status_text ?? device?.deviceStatusText;
    const isDl = device?.type === 'datalogger' || (device?.device_type && String(device.device_type).toUpperCase() === 'DATA_LOGGER');
    const isInv = device?.type === 'inverter' || (device?.device_type && String(device.device_type).toUpperCase() === 'INVERTER');
    if (isDl && plantText) return String(plantText).toUpperCase();
    if (isInv && deviceText) return String(deviceText).toUpperCase();
    return (device?.status || '').toUpperCase();
  };

  // Ngjyrë pike sipas status – Running=green, Idle=yellow, Standby=orange, Offline=red. Energy meter: gjithmonë jeshil (Online).
  const getDeviceStatusStyle = (device) => {
    const isEm = device?.type === 'energymeter' || (device?.device_type && String(device.device_type).toUpperCase() === 'ENERGY_METER');
    if (isEm) return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };
    const label = _getStatusLabelForStyle(device);
    const s = (device?.status || '').toUpperCase();
    if (label.includes('RUNNING') || s === 'ONLINE') return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };  // jeshil
    if (label.includes('IDLE') || s === 'IDLE') return { textClass: 'text-amber-400', dotClass: 'bg-amber-400' };       // qëndruese
    if (label.includes('STANDBY') || s === 'STANDBY') return { textClass: 'text-orange-500', dotClass: 'bg-orange-500' }; // portokalli
    if (label.includes('FAULT') || label.includes('SHUTDOWN') || label.includes('ERROR')) return { textClass: 'text-red-500', dotClass: 'bg-red-500' };
    if (label.includes('OFFLINE') || s === 'OFFLINE') return { textClass: 'text-red-500', dotClass: 'bg-red-500' };      // kuq
    if (s === 'ONLINE') return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };
    if (s === 'STANDBY' || s === 'IDLE') return { textClass: 'text-orange-500', dotClass: 'bg-orange-500' };
    return { textClass: 'text-red-500', dotClass: 'bg-red-500' };
  };

  // Status nga collections reader – datalogger: plant_status_text, inverter: device_status_text (join në backend)
  // Energy meter: gjithmonë "Online". Për inverterët: heqim "No irradiation" nga shfaqja.
  const getDeviceStatusText = (device) => {
    if (!device) return null;
    const isEm = device.type === 'energymeter' || (device.device_type && String(device.device_type).toUpperCase() === 'ENERGY_METER');
    if (isEm) return t('online');
    const plantText = device.plant_status_text ?? device.plantStatusText;
    const deviceText = device.device_status_text ?? device.deviceStatusText;
    const isDl = device.type === 'datalogger' || (device.device_type && String(device.device_type).toUpperCase() === 'DATA_LOGGER');
    const isInv = device.type === 'inverter' || (device.device_type && String(device.device_type).toUpperCase() === 'INVERTER');
    if (isDl && plantText) return String(plantText).trim();
    if (isInv && deviceText) {
      const txt = String(deviceText).trim();
      if (txt.toLowerCase().includes('no irradiation') || txt.includes('40960')) return t('standby');
      return txt;
    }
    return null;
  };

  // Fallback kur nuk ka plant_status_text / device_status_text nga reader
  const getDefaultStatusLabel = (device) => {
    const s = (device?.status || '').toUpperCase();
    if (s === 'ONLINE') return 'Online';
    if (s === 'STANDBY' || s === 'IDLE') return 'Standby';
    return 'Offline';
  };

  const handleTouchStart = (device) => {
    if (!isSuperAdmin) return;
    longPressTimerRef.current = setTimeout(() => {
      openEditModal(device);
    }, 3000);
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const openEditModal = (device) => {
    setEditingDevice(device);

    const normalized = String(device?.type || device?.device_type || '').trim().toLowerCase();

    const type =
      normalized === 'inverter'
        ? 'inverter'
        : normalized === 'energymeter' || normalized === 'energy_meter' || normalized === 'energy-meter'
          ? 'energymeter'
          : 'datalogger';

    setAddDeviceType(type);

    const compEdge = getCompanyEdgeForForm(device.company_id || selectedCompanyId);
    const ownEdge = (device.edge_api_base_url && String(device.edge_api_base_url).trim()) || '';
    const edgeForForm = (type === 'datalogger' || type === 'energymeter')
      ? (ownEdge || compEdge)
      : (device.edge_api_base_url || '');

    setFormData({
      brand: type === 'energymeter' ? energyMeterBrandFromApi(device.brand || '') : device.brand || '',
      serial_number: device.serial_number || '',
      model: device.model || '',
      ip_address: device.ip_address || '',
      name: device.name || '',
      company_id: device.company_id || selectedCompanyId || '',
      data_logger_id: device.data_logger_id || '',
      port: device.port != null ? device.port : 502,
      unit_id: device.unit_id != null && device.unit_id !== '' ? device.unit_id : '',
      power_kw: (device.power_kw ?? device.rated_power_kw) != null ? String(device.power_kw ?? device.rated_power_kw) : '',
      serial_path: device.serial_path || '',
      edge_api_base_url: edgeForForm,
      display_power_unit: (device.display_power_unit && String(device.display_power_unit).toLowerCase() === 'kw') ? 'kw' : 'mw',
      chint_kwh_transform:
        device.chint_kwh_transform != null && device.chint_kwh_transform !== ''
          ? String(device.chint_kwh_transform)
          : '1',
    });

    setPhotoFile(null);
    photoFileRef.current = null;
    setShowAddModal(true);
  };

  const openAddModal = () => {
    setEditingDevice(null);
    setAddDeviceType('');
    setFormData({
      brand: '',
      serial_number: '',
      model: '',
      ip_address: '',
      name: '',
      company_id: selectedCompanyId || '',
      data_logger_id: '',
      port: 502,
      unit_id: '',
      power_kw: '',
      serial_path: '',
      edge_api_base_url: getCompanyEdgeForForm(selectedCompanyId),
      display_power_unit: 'mw',
      chint_kwh_transform: '1',
    });
    setPhotoFile(null);
    photoFileRef.current = null;
    setShowAddModal(true);
  };

  const isKacoInverterBrand = addDeviceType === 'inverter' && String(formData.brand || '').trim().toLowerCase() === 'kaco';

  const isChintEnergyMeterBrand =
    addDeviceType === 'energymeter' && isEnergyMeterChintProfile(formData.brand);

  const commHintText = useMemo(() => {
    if (!addDeviceType) return '';
    if (addDeviceType === 'datalogger') return t('commHintDatalogger');
    if (addDeviceType === 'energymeter') {
      if (isEnergyMeterChintProfile(formData.brand)) return t('commHintChintEnergyMeter');
      return t('commHintEnergyMeter');
    }
    if (addDeviceType === 'inverter') {
      const b = String(formData.brand || '').trim().toLowerCase();
      if (b === 'kaco') return t('commHintKacoInverter');
      if (b === 'growatt' || b === 'huawei') return t('commHintModbusTcpHuaweiGrowatt');
      return t('commHintInverterOtherBrand');
    }
    return '';
  }, [addDeviceType, formData.brand, t]);

  // ✅ auto-select datalogger vetëm kur zgjedh inverter dhe dropdown është bosh (energy meter nuk lidhet me data logger)
  useEffect(() => {
    if (!showAddModal) return;
    if (addDeviceType !== 'inverter') return;
    if (formData.data_logger_id) return;

    const dls = getCompanyDataLoggers();
    if (dls.length > 0) {
      setFormData((prev) => ({ ...prev, data_logger_id: String(dls[0].id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal, addDeviceType, selectedCompanyId]);

  // Edge API nga kompania → autofill te Data logger / Energy meter (nëse fusha është bosh)
  useEffect(() => {
    if (!showAddModal) return;
    if (editingDevice) return;
    if (addDeviceType !== 'datalogger' && addDeviceType !== 'energymeter') return;
    const efu = getCompanyEdgeForForm(selectedCompanyId);
    if (!efu) return;
    setFormData((prev) => {
      if (String(prev.edge_api_base_url || '').trim()) return prev;
      return { ...prev, edge_api_base_url: efu };
    });
  }, [showAddModal, addDeviceType, selectedCompanyId, companies, editingDevice, getCompanyEdgeForForm]);

  const handleDeleteDevice = async () => {
    if (!editingDevice) return;
    const confirmMsg = addDeviceType === 'datalogger'
      ? t('confirmDeleteDatalogger')
      : t('confirmDeleteDevice');
    const ok = await confirm({ title: t('confirmDeleteTitle') || 'Konfirmo fshirjen', message: confirmMsg, confirmLabel: t('delete') || 'Fshi', cancelLabel: t('cancel') || 'Jo' });
    if (!ok) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = addDeviceType === 'datalogger'
        ? `/api/devices/data-loggers/${editingDevice.id}`
        : addDeviceType === 'inverter'
          ? `/api/devices/inverters/${editingDevice.id}`
          : `/api/devices/energy-meters/${editingDevice.id}`;

      await axios.delete(`${BACKEND_URL}${endpoint}`, { headers });
      toast.success(t('deviceDeleted'));
      setShowAddModal(false);
      setEditingDevice(null);
      await fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isNetworkError = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const fallback = isNetworkError ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : 'Gabim gjatë fshirjes';
      const msg = isNetworkError ? fallback : (Array.isArray(detail) ? detail.map((d) => d.msg || d).join(', ') : (detail ?? error.message ?? 'Gabim gjatë fshirjes'));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDevice = async () => {
    if (!addDeviceType) return;
    if (!selectedCompanyId) {
      toast.error('Zgjidh një kompani!');
      return;
    }

    const emBrand = energyMeterBrandToApi(formData.brand).trim().toLowerCase();
    if (addDeviceType === 'energymeter' && emBrand === EM_BRAND_CHINT_API && !(formData.serial_path || '').trim()) {
      toast.error(t('chintSerialPathRequired'));
      return;
    }

    const fileToUpload = photoFile || photoFileRef.current;
    const deviceIdForLog = editingDevice?.id;
    // #region agent log
    fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:handleSaveDeviceStart', message: 'Save device started', data: { hasPhotoFile: !!photoFile, hasRefFile: !!photoFileRef.current, fileToUpload: !!fileToUpload, deviceId: deviceIdForLog, editing: !!editingDevice }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H4' }) }).catch(() => { });
    // #endregion

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const endpoint =
        addDeviceType === 'datalogger'
          ? '/api/devices/data-loggers'
          : addDeviceType === 'inverter'
            ? '/api/devices/inverters'
            : '/api/devices/energy-meters';

      const port = formData.port != null && formData.port !== '' ? Number(formData.port) : 502;
      const unitId = formData.unit_id != null && formData.unit_id !== '' ? Number(formData.unit_id) : (addDeviceType === 'datalogger' ? 0 : undefined);
      const serialNumber = editingDevice ? (formData.serial_number || editingDevice.serial_number) : (formData.serial_number || formData.name || `${addDeviceType}-${Date.now()}`);
      const payload = {
        serial_number: serialNumber,
        name: formData.name || formData.brand || serialNumber,
        brand: addDeviceType === 'energymeter' ? energyMeterBrandToApi(formData.brand) : formData.brand,
        model: formData.model,
        company_id: selectedCompanyId,
        port,
        ...(unitId !== undefined ? { unit_id: unitId } : {}),
        ...(addDeviceType === 'datalogger'
          ? { ip_address: formData.ip_address }
          : addDeviceType === 'energymeter'
            ? { ip_address: formData.ip_address || undefined }
            : { data_logger_id: formData.data_logger_id || getCompanyDataLoggers()[0]?.id }
        ),
        ...(addDeviceType === 'inverter' && formData.power_kw ? { power_kw: parseFloat(formData.power_kw), rated_power_kw: parseFloat(formData.power_kw) } : {}),
        ...(isKacoInverterBrand ? { serial_path: (formData.serial_path || '').trim() || null } : {}),
        ...(addDeviceType === 'energymeter'
          ? emBrand === EM_BRAND_CHINT_API
            ? {
                serial_path: (formData.serial_path || '').trim() || null,
                modbus_profile: 'chint_dtsu666',
              }
            : { serial_path: null, modbus_profile: null }
          : {}),
        ...((addDeviceType === 'datalogger' || addDeviceType === 'energymeter')
          ? {
              edge_api_base_url: (formData.edge_api_base_url || '').trim() || null,
              display_power_unit: formData.display_power_unit === 'kw' ? 'kw' : 'mw'
            }
          : {}),
        ...(addDeviceType === 'energymeter' && emBrand === EM_BRAND_CHINT_API
          ? {
              chint_kwh_transform: (() => {
                const raw = String(formData.chint_kwh_transform ?? '').trim().replace(',', '.');
                const v = parseFloat(raw);
                return Number.isFinite(v) && v > 0 ? v : 1;
              })(),
            }
          : {})
      };

      if (addDeviceType === 'inverter' && !payload.data_logger_id) {
        toast.error('Zgjidh Data Logger!');
        setSaving(false);
        return;
      }

      let deviceId;
      if (editingDevice) {
        await axios.put(`${BACKEND_URL}${endpoint}/${editingDevice.id}`, payload, { headers });
        deviceId = editingDevice.id;
      } else {
        const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload, { headers });
        deviceId = response.data?.id;
      }

      if (fileToUpload && deviceId) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', fileToUpload);

        const deviceTypeForUpload =
          addDeviceType === 'datalogger' ? 'data-logger' : addDeviceType === 'energymeter' ? 'energy-meter' : 'inverter';

        // Kur hapet nga localhost dhe BACKEND_URL është production, përdor backend lokal për ngarkim (shmang 405)
        const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const uploadBaseUrl = BACKEND_URL;
        const uploadUrl = `${uploadBaseUrl}/api/upload/photo/${deviceTypeForUpload}/${deviceId}`;
        // #region agent log
        fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadBefore', message: 'Before photo upload', data: { method: 'POST', uploadUrl, BACKEND_URL, deviceTypeForUpload, deviceId, hasToken: !!headers.Authorization }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H1' }) }).catch(() => { });
        // #endregion
        try {
          const uploadRes = await axios.post(uploadUrl, formDataUpload, {
            headers: { Authorization: headers.Authorization }
          });
          // #region agent log
          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadSuccess', message: 'Photo upload OK', data: { status: uploadRes.status, photo_url: uploadRes.data?.photo_url }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H2' }) }).catch(() => { });
          // #endregion
          if (uploadRes.data?.photo_url) {
            console.log('Foto u ruajt:', uploadRes.data.photo_url);
          }
        } catch (uploadErr) {
          const status = uploadErr.response?.status;
          const detail = uploadErr.response?.data?.detail || uploadErr.message;
          const reqUrl = uploadErr.config?.url;
          const reqMethod = uploadErr.config?.method;
          // #region agent log
          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadError', message: 'Photo upload failed', data: { status, detail, uploadUrl, reqUrl, reqMethod, BACKEND_URL }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H1' }) }).catch(() => { });
          // #endregion
          console.error('Gabim ngarkimi foto:', { status, detail, url: uploadUrl, backend: BACKEND_URL });
          const msg = status === 401
            ? 'Foto: nuk je i kyçur ose token i skaduar. Dil dhe kyçu përsëri.'
            : status === 404
              ? 'Foto: endpoint ose pajisja nuk u gjet. Kontrollo që backend-i është i përditësuar (nisje përsëri).'
              : status === 405
                ? 'Foto: Method Not Allowed. Backend-i duhet rinovuar (mbyll dhe nis përsëri serverin).'
                : status === 500
                  ? `Foto: gabim serveri: ${detail}`
                  : `Foto nuk u ruajt (${status || 'rrjet'}): ${detail}`;
          toast.error(msg);
          setSaving(false);
          return;
        }
      }

      await fetchData();
      setShowAddModal(false);
      toast.success(editingDevice ? 'Pajisja u përditësua!' : 'Pajisja u shtua!');
    } catch (error) {
      const isNetwork = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      console.error('Gabim ruajtje pajisje:', { status, detail });
      toast.error(isNetwork ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : (status ? `[${status}] ${detail}` : detail) || 'Gabim');
    } finally {
      setSaving(false);
    }
  };

  const getMockInverterData = (device) => {
    const base = device?.installed_capacity_kw || device?.rated_power_kw || 5;
    return {
      pvPower: (base * 0.85).toFixed(3),
      todayYield: (base * 5.2).toFixed(1),
      totalYield: (base * 920).toFixed(1),
      voltage: { r: 230.5, s: 231.4, t: 230.5 },
      current: { r: 6.8, s: 6.8, t: 6.9 },
      power: { r: 1.567, s: 1.571, t: 1.540 }
    };
  };

  const getChartData = () =>
    analysisPeriod === 'day'
      ? Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        value: i >= 6 && i <= 18 ? Math.sin((i - 6) * Math.PI / 12) * 50 + Math.random() * 10 : Math.random() * 2
      }))
      : Array.from({ length: 30 }, (_, i) => ({ day: i + 1, value: Math.random() * 100 + 50 }));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Duke ngarkuar...</p>
        </div>
      </Layout>
    );
  }

  // Mobile Detail View
  const MobileDetailView = () => {
    const devices = getFilteredDevices();
    const device = selectedDevice;
    const mockData = getMockInverterData(device);
    const chartData = getChartData();
    const maxValue = Math.max(...chartData.map(d => d.value));

    return (
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gradient-to-b dark:from-slate-900 dark:to-black">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between mb-3">
            <select
              value={selectedCompany?.id || ''}
              onChange={(e) => {
                const company = companies.find(c => String(c.id) === String(e.target.value));
                switchCompany(company);
                setDeviceView('list');
              }}
              className="text-lg font-bold bg-transparent border-none text-gray-900 dark:text-white focus:outline-none cursor-pointer pr-6"
            >
              {companies.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <button onClick={() => setDeviceView('list')} className="text-gray-500 dark:text-white text-xl">✕</button>
          </div>
          <div className="flex gap-2">
            {[{ id: 'all', labelKey: 'allDevices' }, { id: 'inverter', labelKey: 'inverterTab' }, { id: 'ems', labelKey: 'emsTab' }].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setDeviceView('list'); }}
                className={`px-4 py-2 rounded-full text-sm font-medium ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <span className="inline-block px-3 py-1 bg-green-500 text-white text-xs font-bold rounded mb-4">Normal</span>

          <div
            className="relative mb-4"
            onTouchStart={() => handleTouchStart(device)}
            onTouchEnd={handleTouchEnd}
            onMouseDown={() => handleTouchStart(device)}
            onMouseUp={handleTouchEnd}
            onMouseLeave={handleTouchEnd}
          >
            <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-6 flex items-center justify-center h-40 ${cardShadowClass}`}>
              <img src={getPhotoUrl(device)} alt={device?.name || 'device'} className="max-h-32 object-contain" />
              {isSuperAdmin && <div className="absolute top-2 right-2 bg-gray-200 dark:bg-black/50 text-gray-600 dark:text-white text-[8px] px-1.5 py-0.5 rounded">3s edit</div>}
            </div>

            {devices.length > 1 && (
              <>
                <button
                  onClick={() => {
                    const idx = (currentDeviceIndex - 1 + devices.length) % devices.length;
                    setCurrentDeviceIndex(idx);
                    setSelectedDevice(devices[idx]);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-transparent rounded-full w-8 h-8 flex items-center justify-center"
                >
                  ‹
                </button>

                <button
                  onClick={() => {
                    const idx = (currentDeviceIndex + 1) % devices.length;
                    setCurrentDeviceIndex(idx);
                    setSelectedDevice(devices[idx]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-transparent rounded-full w-8 h-8 flex items-center justify-center"
                >
                  ›
                </button>

                <div className="absolute bottom-2 right-4 text-gray-500 dark:text-gray-400 text-sm">
                  {currentDeviceIndex + 1}/{devices.length}
                </div>
              </>
            )}
          </div>

          <div className="text-center mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm">{device?.serial_number} ›</p>
            <p className="text-gray-400 dark:text-gray-500 text-xs">Updated 1 minute ago</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-4 ${cardShadowClass}`}>
              <div className="relative w-24 h-24 mx-auto mb-2">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" className="dark:stroke-gray-700" strokeWidth="8" fill="none" />
                  <circle cx="48" cy="48" r="40" stroke="#3b82f6" strokeWidth="8" fill="none" strokeDasharray={`${(mockData.pvPower / 10) * 251} 251`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">{mockData.pvPower}</span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">kW</span>
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">PV Power</p>
            </div>

            <div className="space-y-3">
              <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-3 ${cardShadowClass}`}>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today PV Yield</p>
                <p className="text-xl font-bold text-cyan-500">{mockData.todayYield}<span className="text-sm ml-1">kWh</span></p>
              </div>
              <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-3 ${cardShadowClass}`}>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total PV Yield</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{(mockData.totalYield / 1000).toFixed(1)}<span className="text-sm ml-1">MWh</span></p>
              </div>
            </div>
          </div>

          <div className={`bg-white dark:bg-[#1E293B] rounded-xl mb-3 ${cardShadowClass}`}>
            <button onClick={() => setShowDetails(!showDetails)} className="w-full flex items-center justify-between p-4">
              <span className="font-medium text-gray-900 dark:text-white">Detailed Parameters</span>
              <span className="text-gray-400">{showDetails ? '∧' : '›'}</span>
            </button>
            {showDetails && (
              <div className="px-4 pb-4">
                <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">AC Information</p>
                  <div className="grid grid-cols-4 gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <div></div><div className="text-gray-500">V</div><div className="text-gray-500">A</div><div className="text-gray-500">kW</div>
                    {['R', 'S', 'T'].map(p => (
                      <Fragment key={p}>
                        <div className="font-medium">{p}</div>
                        <div>{mockData.voltage[p.toLowerCase()]}</div>
                        <div>{mockData.current[p.toLowerCase()]}</div>
                        <div>{mockData.power[p.toLowerCase()]}</div>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white dark:bg-[#1E293B] rounded-xl ${cardShadowClass}`}>
            <button onClick={() => setShowAnalysis(!showAnalysis)} className="w-full flex items-center justify-between p-4">
              <span className="font-medium text-gray-900 dark:text-white">Analysis Graph</span>
              <span className="text-gray-400">{showAnalysis ? '∧' : '∨'}</span>
            </button>
            {showAnalysis && (
              <div className="px-4 pb-4">
                <div className="flex gap-2 mb-4">
                  {['day', 'month', 'year', 'lifetime'].map(p => (
                    <button
                      key={p}
                      onClick={() => setAnalysisPeriod(p)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${analysisPeriod === p ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button className="text-gray-400">◀</button>
                  <span className="text-sm text-gray-700 dark:text-white">2026-01-22</span>
                  <button className="text-gray-400">▶</button>
                </div>
                <div className="h-40 flex items-end gap-px">
                  {chartData.map((d, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t" style={{ height: `${(d.value / maxValue) * 100}%` }}></div>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const MobileListView = () => {
    const devices = getFilteredDevices();
    return (
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]">
          <div className="flex items-center justify-between mb-3">
            <div className="relative">
              <button onClick={() => setShowCompanyDropdown(!showCompanyDropdown)} className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">{selectedCompany?.name || (companies.length === 0 && !loading ? 'Nuk ka kompani' : 'Loading...')}</span>
                <svg className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCompanyDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {companies.map(company => (
                    <button
                      key={company.id}
                      onClick={() => switchCompany(company)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${String(selectedCompany?.id) === String(company.id)
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => navigate(-1)} className="w-7 h-7 shrink-0 rounded-full bg-[#e8e8e8] dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 flex items-center justify-center text-gray-600 dark:text-white text-sm font-medium" aria-label="Kthehu">
              ✕
            </button>
          </div>

          {/* Mobile: EMS dhe + në të njëjtën rresht; butonat +20% për tap më të lehtë */}
          <div className="flex items-center gap-2 flex-nowrap">
            <div className="flex gap-2 flex-1 min-w-0">
              {[{ id: 'all', labelKey: 'allDevices' }, { id: 'inverter', labelKey: 'inverterTab' }, { id: 'ems', labelKey: 'emsTab' }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition shrink-0 ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>
            {isSuperAdmin && (
              <button onClick={openAddModal} className="w-7 h-7 shrink-0 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow flex items-center justify-center border border-blue-600/30" aria-label={t('addDevice')}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-3 bg-[#f0f0f0] dark:bg-[#0f172a]/80">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {activeTab === 'inverter' ? t('inverterBattery') : activeTab === 'ems' ? t('ems') : t('allDevices')}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {devices.map((device, index) => (
              <div
                key={device.id}
                data-testid={`device-card-${device.id}`}
                onClick={() => {
                  if (device.type === 'datalogger') navigate(`/devices/${device.id}`);
                  else if (device.type === 'inverter') navigate(`/devices/${device.data_logger_id}/inverter/${device.id}`);
                  else if (device.type === 'energymeter')
                    navigate(buildEnergyMeterDetailPath(device) || `/devices/${device.data_logger_id || 'standalone'}/energy-meter/${device.id}`);
                }}
                onTouchStart={() => handleTouchStart(device)}
                onTouchEnd={handleTouchEnd}
                onMouseDown={() => handleTouchStart(device)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                className={`relative bg-white dark:bg-[#1e293b] rounded-xl p-3 cursor-pointer transition ${cardShadowClass}`}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEdgePopupDevice(device); setShowEdgePopup(true); }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 flex items-center justify-center text-gray-600 dark:text-gray-300 text-xs font-medium z-10"
                  aria-label="Të dhënat e pajisjes"
                >
                  ℹ
                </button>
                <div className="w-full h-20 mb-3 rounded-lg overflow-hidden bg-white dark:bg-[#1e293b] flex items-center justify-center">
                  {device.photo_url ? (
                    <img src={getPhotoUrl(device)} alt={device.name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <svg className="w-12 h-12 text-gray-300 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  )}
                </div>

                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1.5">
                  {device.type === 'datalogger' ? device.name : device.type === 'energymeter' ? (device.name || device.model || `Energy Meter-${index + 1}`) : (device.model || device.name || `Inverter-${index + 1}`)}
                </h4>
                {device.type === 'inverter' && (() => {
                  const dlName = getDataloggerNameForInverter(device);
                  if (!dlName) return null;
                  return (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 truncate" title={`DL: ${dlName}`}>
                      DL: {dlName}
                    </p>
                  );
                })()}

                <div className="flex items-center gap-2 text-xs flex-wrap">
                  {(() => {
                    const style = getDeviceStatusStyle(device);
                    const statusLabel = getDeviceStatusText(device) || getDefaultStatusLabel(device);
                    return (
                      <span className={`flex items-center gap-1 ${style.textClass}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dotClass}`}></span>
                        <span className="truncate" title={statusLabel}>{statusLabel}</span>
                      </span>
                    );
                  })()}
                  <span className="text-gray-400 shrink-0">|</span>
                  <span className="text-gray-500 dark:text-gray-400 shrink-0">
                    {device.grid_status === 'ON_GRID' ? 'ON GRID' : 'OFF GRID'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {devices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>{t('noDevices')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const WebListView = () => {
    const devices = getFilteredDevices();
    return (
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
        <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2 text-left"
              >
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {selectedCompany?.name || (companies.length === 0 && !loading ? 'Nuk ka kompani' : 'Loading...')}
                </span>
                <svg className={`w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCompanyDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => switchCompany(company)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${String(selectedCompany?.id) === String(company.id)
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              {[{ id: 'all', labelKey: 'allDevices' }, { id: 'inverter', labelKey: 'inverterTab' }, { id: 'ems', labelKey: 'emsTab' }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); if (tab.id !== 'inverter') setSelectedDatalogger(null); }}
                  className={`px-5 py-2 rounded-lg text-sm font-medium ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>
            {isSuperAdmin && (
              <button onClick={openAddModal} className="px-5 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shrink-0">
                <span className="text-lg leading-none">+</span> {t('addDevice')}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 p-4">
          {/* Web: 6 kolona × 3 rreshta = 18 pajisje pa scroll; All / Inverter / EMS */}
          <div className="grid grid-cols-6 gap-2 h-full content-start">
            {devices.map((device, index) => (
              <div
                key={device.id}
                data-testid={`device-card-${device.id}`}
                onClick={() => {
                  if (device.type === 'datalogger') navigate(`/devices/${device.id}`);
                  else if (device.type === 'inverter') navigate(`/devices/${device.data_logger_id}/inverter/${device.id}`);
                  else if (device.type === 'energymeter')
                    navigate(buildEnergyMeterDetailPath(device) || `/devices/${device.data_logger_id || 'standalone'}/energy-meter/${device.id}`);
                }}
                onMouseDown={() => handleTouchStart(device)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                className="relative bg-white dark:bg-[#1e293b] rounded-lg p-2 cursor-pointer hover:scale-[1.02] transition flex flex-col justify-between min-h-0"
                style={{ height: 'calc((100vh - 200px) / 3 - 6px)' }}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEdgePopupDevice(device); setShowEdgePopup(true); }}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 flex items-center justify-center text-gray-600 dark:text-gray-300 text-[10px] font-medium z-10"
                  aria-label="Të dhënat e pajisjes"
                >
                  ℹ
                </button>
                {device.photo_url ? (
                  <div className="flex-1 min-h-0 rounded overflow-hidden bg-white dark:bg-[#1e293b] flex items-center justify-center mb-1">
                    <img src={getPhotoUrl(device)} alt={device.name} className="max-h-full max-w-full object-contain" />
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-1 min-h-0">
                    <div className="w-8 h-6 bg-white dark:bg-[#1e293b] rounded flex items-center justify-center border border-gray-200 dark:border-transparent shrink-0">
                      <div className="w-5 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                    </div>
                    <div className="w-1 h-6 bg-gray-300 dark:bg-gray-500 rounded shrink-0"></div>
                  </div>
                )}

                <div className="min-h-0 shrink-0">
                  <h4 className="font-semibold text-xs text-gray-900 dark:text-white mb-0.5 truncate">
                    {device.type === 'inverter' ? (device.model || device.name || `Inverter-${index + 1}`) : device.type === 'energymeter' ? (device.name || device.model || `Energy Meter-${index + 1}`) : device.name}
                  </h4>
                  {device.type === 'inverter' && (() => {
                    const dlName = getDataloggerNameForInverter(device);
                    if (!dlName) return null;
                    return (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 truncate" title={`DL: ${dlName}`}>
                        DL: {dlName}
                      </p>
                    );
                  })()}
                  <div className="flex items-center gap-1 text-[10px] flex-wrap">
                    {(() => {
                      const style = getDeviceStatusStyle(device);
                      const statusLabel = getDeviceStatusText(device) || getDefaultStatusLabel(device);
                      return (
                        <span className={`font-medium flex items-center gap-0.5 ${style.textClass}`}>
                          <span className={`w-1 h-1 rounded-full shrink-0 ${style.dotClass}`}></span>
                          <span className="truncate" title={statusLabel}>{statusLabel}</span>
                        </span>
                      );
                    })()}
                    <span className="text-gray-400 text-[9px] shrink-0">{device.type === 'energymeter' ? 'Energy Meter' : device.grid_status === 'ON_GRID' ? 'ON GRID' : 'OFF GRID'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {devices.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <p className="text-lg">Nuk ka pajisje</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {isMobile ? <MobileListView /> : <WebListView />}

      {/* Add/Edit Device Modal */}
      {showAddModal && (
        <div className={`fixed inset-0 bg-black/60 flex items-center justify-center z-50 ${isMobile ? 'p-2' : 'p-4'}`}>
          {isMobile ? (
            /* MOBILE: scroll form, footer me Anulo/Ruaj gjithmonë i dukshëm; Data Logger / Energy Meter: hapësira ~15% më pak që të mos bëhet scroll */
            (() => {
              const compactForm = addDeviceType === 'datalogger' || addDeviceType === 'energymeter';
              const fieldGap = compactForm ? 'space-y-3' : 'space-y-4';
              const panelPy = compactForm ? 'py-1.5' : 'py-2';
              const inputH = compactForm ? 28 : 32;
              const photoMb = compactForm ? 'mb-2' : 'mb-4';
              return (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm flex flex-col max-h-[95vh] overflow-hidden">
                  <div className={`flex items-center justify-between px-2 ${panelPy} border-b border-gray-200 dark:border-slate-700 flex-shrink-0`}>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{editingDevice ? t('editDevice') : t('addDevice')}</h3>
                    <button type="button" onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 shrink-0" aria-label={t('close')}>✕</button>
                  </div>
                  <div className={`px-2 ${panelPy} ${fieldGap} overflow-y-auto min-h-0 flex-1`}>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('typeRequired')}</label>
                      <select value={addDeviceType} onChange={(e) => {
                        const v = e.target.value;
                        setAddDeviceType(v);
                        if (!editingDevice && v === 'datalogger') setFormData((prev) => ({ ...prev, brand: 'Dio Logger', serial_path: '' }));
                        if (!editingDevice && v === 'inverter') setFormData((prev) => ({ ...prev, brand: 'Huawei', serial_path: '' }));
                        if (!editingDevice && v === 'energymeter') setFormData((prev) => ({ ...prev, brand: '', serial_path: '' }));
                      }} disabled={!!editingDevice}
                        style={{ height: `${inputH}px` }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                        <option value="">{t('selectType')}</option>
                        <option value="datalogger">{t('dataLogger')}</option>
                        <option value="inverter">{t('inverter')}</option>
                        <option value="energymeter">{t('energyMeter')}</option>
                      </select>
                    </div>
                    {addDeviceType && (<>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('brand')}</label>
                        <select value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                          <option value="">{t('selectBrand')}</option>
                          {effectiveBrandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        {commHintText ? (
                          <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-1">{commHintText}</p>
                        ) : null}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('model')}</label>
                        <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder={t('model')} />
                      </div>
                      {addDeviceType === 'datalogger' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      ) : addDeviceType === 'inverter' && getCompanyDataLoggers().length > 0 ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('dataLoggerRequired')}</label>
                          <select value={formData.data_logger_id} onChange={(e) => setFormData({ ...formData, data_logger_id: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                            <option value="">{t('select')}</option>
                            {getCompanyDataLoggers().map(dl => (<option key={dl.id} value={dl.id}>{dl.name}</option>))}
                          </select>
                        </div>
                      ) : null}
                      {addDeviceType === 'inverter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('powerKw')}</label>
                          <input type="number" min={1} step={1} value={formData.power_kw} onChange={(e) => setFormData({ ...formData, power_kw: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="0" />
                        </div>
                      )}
                      {isKacoInverterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('serialPathRtu')}</label>
                          <input type="text" value={formData.serial_path} onChange={(e) => setFormData({ ...formData, serial_path: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="/dev/ttyUSB0" />
                        </div>
                      )}
                      {addDeviceType === 'energymeter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {isChintEnergyMeterBrand ? `${t('ipAddress')} (${t('optional')})` : t('ipAddress')}
                          </label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder={isChintEnergyMeterBrand ? '0.0.0.0 ose etiketë' : '192.168.1.100'} />
                        </div>
                      )}
                      {isChintEnergyMeterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('serialPathRtu')} *</label>
                          <input type="text" value={formData.serial_path} onChange={(e) => setFormData({ ...formData, serial_path: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white font-mono"
                            placeholder="/dev/ttyS1"
                          />
                        </div>
                      )}
                      {isChintEnergyMeterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Transform</label>
                          <input type="number" inputMode="decimal" min={0} step="any" value={formData.chint_kwh_transform}
                            onChange={(e) => setFormData({ ...formData, chint_kwh_transform: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="1"
                          />
                          <p className="text-[10px] leading-snug text-gray-400 mt-0.5">Scales Chint total forward/reverse kWh (0x101E / 0x1028) before storage. Default 1.</p>
                        </div>
                      )}
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div className="flex gap-2">
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('port')}</label>
                            <input type="number" min={1} max={65535} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                              style={{ height: `${inputH}px` }}
                              className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="502" />
                          </div>
                          <div className="w-[88px] shrink-0">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('powerDisplayUnit')}</label>
                            <select value={formData.display_power_unit} onChange={(e) => setFormData({ ...formData, display_power_unit: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className="w-full px-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                              <option value="mw">MW</option>
                              <option value="kw">kW</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Edge API (Raspberry)</label>
                          <input type="text" value={formData.edge_api_base_url} onChange={(e) => setFormData({ ...formData, edge_api_base_url: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="http://127.0.0.1:9002"
                          />
                          <p className="text-[10px] leading-snug text-gray-400 mt-0.5">Opsionale — URL e api.py në Pi (HTTP). Bosh = EDGE_API_URL në server.</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('unitId')}</label>
                        <input type="number" min={0} value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value === '' ? '' : Number(e.target.value) })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                          placeholder={addDeviceType === 'datalogger' ? '0' : addDeviceType === 'inverter' ? '12' : '1'} />
                      </div>
                      <div className={photoMb}>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setPhotoFile(f);
                          photoFileRef.current = f;
                        }} className="hidden" />
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-dashed border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400">
                          {photoFile ? `📷 ${photoFile.name.slice(0, 20)}` : t('uploadPhotoShort')}
                        </button>
                      </div>
                    </>)}
                  </div>
                  <div className={`flex gap-2 px-2 ${panelPy} border-t border-gray-200 dark:border-slate-700 flex-shrink-0`}>
                    {editingDevice && (
                      <button onClick={handleDeleteDevice} disabled={saving} style={{ height: '32px' }} className="flex-1 min-w-0 bg-red-500 text-white rounded text-sm font-medium disabled:opacity-50">{t('delete')}</button>
                    )}
                    <button onClick={() => setShowAddModal(false)} style={{ height: '32px' }} className="flex-1 min-w-0 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded text-sm font-medium">{t('cancel')}</button>
                    <button onClick={handleSaveDevice} disabled={!addDeviceType || saving}
                      style={{ height: '32px' }} className="flex-1 min-w-0 bg-green-500 text-white rounded text-sm font-medium disabled:opacity-50">{saving ? '...' : t('save')}</button>
                  </div>
                </div>
              );
            })()
          ) : (
            /* WEB: 2 column layout - compact fields */
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{editingDevice ? t('editDevice') : t('addDevice')}</h3>
                <button onClick={() => setShowAddModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600"><span className="text-gray-500 dark:text-gray-400">✕</span></button>
              </div>
              <div className="p-4">
                {/* Web: fillim vetëm zgjedhja e llojit (njëlloj si mobile), pastaj fushat e tjera */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('deviceTypeRequired')}</label>
                  <select value={addDeviceType} onChange={(e) => {
                    const v = e.target.value;
                    setAddDeviceType(v);
                    if (!editingDevice && v === 'datalogger') setFormData((prev) => ({ ...prev, brand: 'Dio Logger', serial_path: '' }));
                    if (!editingDevice && v === 'inverter') setFormData((prev) => ({ ...prev, brand: 'Huawei', serial_path: '' }));
                    if (!editingDevice && v === 'energymeter') setFormData((prev) => ({ ...prev, brand: '', serial_path: '' }));
                  }} disabled={!!editingDevice}
                    style={{ height: '32px' }}
                    className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="">{t('selectType')}</option>
                    <option value="datalogger">{t('dataLogger')}</option>
                    <option value="inverter">{t('inverter')}</option>
                    <option value="energymeter">{t('energyMeter')}</option>
                  </select>
                </div>
                {addDeviceType && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {/* Kolona e majtë */}
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('brand')}</label>
                        <select value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                          <option value="">{t('selectBrand')}</option>
                          {effectiveBrandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        {commHintText ? (
                          <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-1">{commHintText}</p>
                        ) : null}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('model')}</label>
                        <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder={t('model')} />
                      </div>
                      {addDeviceType === 'datalogger' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      ) : addDeviceType === 'inverter' && getCompanyDataLoggers().length > 0 ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('dataLoggerRequired')}</label>
                          <select value={formData.data_logger_id} onChange={(e) => setFormData({ ...formData, data_logger_id: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                            <option value="">{t('select')}</option>
                            {getCompanyDataLoggers().map(dl => (<option key={dl.id} value={dl.id}>{dl.name}</option>))}
                          </select>
                        </div>
                      ) : null}
                      {addDeviceType === 'inverter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('powerKw')}</label>
                          <input type="number" min={1} step={1} value={formData.power_kw} onChange={(e) => setFormData({ ...formData, power_kw: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="0" />
                        </div>
                      )}
                      {isKacoInverterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('serialPathRtu')}</label>
                          <input type="text" value={formData.serial_path} onChange={(e) => setFormData({ ...formData, serial_path: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="/dev/ttyUSB0" />
                        </div>
                      )}
                      {addDeviceType === 'energymeter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                            {isChintEnergyMeterBrand ? `${t('ipAddress')} (${t('optional')})` : t('ipAddress')}
                          </label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder={isChintEnergyMeterBrand ? '0.0.0.0 ose etiketë' : '192.168.1.100'} />
                        </div>
                      )}
                      {isChintEnergyMeterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('serialPathRtu')} *</label>
                          <input type="text" value={formData.serial_path} onChange={(e) => setFormData({ ...formData, serial_path: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white font-mono"
                            placeholder="/dev/ttyS1"
                          />
                        </div>
                      )}
                      {isChintEnergyMeterBrand && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Transform</label>
                          <input type="number" inputMode="decimal" min={0} step="any" value={formData.chint_kwh_transform}
                            onChange={(e) => setFormData({ ...formData, chint_kwh_transform: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="1"
                          />
                          <p className="text-[10px] leading-snug text-gray-500 dark:text-slate-500 mt-0.5">Scales Chint total forward/reverse kWh (0x101E / 0x1028) before storage. Default 1.</p>
                        </div>
                      )}
                    </div>
                    {/* Kolona e djathtë */}
                    <div className="space-y-2">
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div className="flex gap-2">
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('port')}</label>
                            <input type="number" min={1} max={65535} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                              style={{ height: '32px' }}
                              className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="502" />
                          </div>
                          <div className="w-[96px] shrink-0">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('powerDisplayUnit')}</label>
                            <select value={formData.display_power_unit} onChange={(e) => setFormData({ ...formData, display_power_unit: e.target.value })}
                              style={{ height: '32px' }}
                              className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                              <option value="mw">MW</option>
                              <option value="kw">kW</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Edge API (Raspberry)</label>
                          <input type="text" value={formData.edge_api_base_url} onChange={(e) => setFormData({ ...formData, edge_api_base_url: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="http://127.0.0.1:9002"
                          />
                          <p className="text-[10px] leading-snug text-gray-400 mt-0.5">Opsionale — URL e api.py (HTTP). Bosh = EDGE_API_URL.</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('unitId')}</label>
                        <input type="number" min={0} value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value === '' ? '' : Number(e.target.value) })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                          placeholder={addDeviceType === 'datalogger' ? '0' : addDeviceType === 'inverter' ? '12' : '1'} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('photo')}</label>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setPhotoFile(file);
                          photoFileRef.current = file;
                          // #region agent log
                          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:fileInputChange', message: 'File input onChange', data: { filesLength: e.target.files?.length, fileName: file?.name }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H3' }) }).catch(() => { });
                          // #endregion
                        }} className="hidden" />
                        <button type="button" onClick={() => {
                          // #region agent log
                          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:fotoButtonClick', message: 'Foto button clicked', data: { refExists: !!fileInputRef.current }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H2' }) }).catch(() => { });
                          // #endregion
                          fileInputRef.current?.click();
                        }}
                          style={{ height: '36px' }}
                          className="w-full px-3 text-sm font-medium rounded-lg border-2 border-dashed border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 hover:border-cyan-600 dark:hover:border-cyan-300 transition-colors cursor-pointer flex items-center justify-center gap-2">
                          <span aria-hidden>📷</span>
                          <span>{photoFile ? photoFile.name : t('selectFile')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                {editingDevice && (
                  <button onClick={handleDeleteDevice} disabled={saving} style={{ height: '32px' }} className="flex-1 bg-red-500 text-white rounded font-medium hover:bg-red-600 disabled:opacity-50 text-sm">{t('delete')}</button>
                )}
                <button onClick={() => setShowAddModal(false)} style={{ height: '32px' }} className="flex-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded font-medium hover:bg-gray-200 dark:hover:bg-slate-600 text-sm">{t('cancel')}</button>
                <button onClick={handleSaveDevice} disabled={!addDeviceType || saving}
                  style={{ height: '32px' }} className="flex-1 bg-green-500 text-white rounded font-medium hover:bg-green-600 disabled:opacity-50 text-sm">{saving ? t('saving') : t('save')}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Popup të dhënat e pajisjes (Edge / Raspberry) */}
      {showEdgePopup && edgePopupDevice && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowEdgePopup(false)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full p-4 border border-gray-200 dark:border-slate-600"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                {t("deviceData")}
              </h4>
              <button
                type="button"
                onClick={() => setShowEdgePopup(false)}
                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 text-sm"
                aria-label={t("close")}
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 truncate">
              {edgePopupDevice.name || edgePopupDevice.serial_number}
            </p>
            <dl className="space-y-2 text-sm">
              {edgePopupDevice.ip_address != null &&
                edgePopupDevice.ip_address !== "" && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500 dark:text-gray-400">IP</dt>
                    <dd className="font-mono text-gray-900 dark:text-white">
                      {edgePopupDevice.ip_address}
                    </dd>
                  </div>
                )}
              {edgePopupDevice.unit_id != null && (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t("unitId")}
                  </dt>
                  <dd className="font-mono text-gray-900 dark:text-white">
                    {edgePopupDevice.unit_id}
                  </dd>
                </div>
              )}
              {edgePopupDevice.serial_path != null && String(edgePopupDevice.serial_path).trim() !== "" && (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">{t("serialPathRtu")}</dt>
                  <dd className="font-mono text-gray-900 dark:text-white text-right break-all max-w-[60%]">
                    {edgePopupDevice.serial_path}
                  </dd>
                </div>
              )}
              {edgePopupDevice.modbus_profile != null && String(edgePopupDevice.modbus_profile).trim() !== "" && (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">Modbus profile</dt>
                  <dd className="font-mono text-gray-900 dark:text-white text-right break-all max-w-[60%]">
                    {edgePopupDevice.modbus_profile}
                  </dd>
                </div>
              )}
              {edgePopupDevice.power_kw != null && (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t("power")}
                  </dt>
                  <dd className="font-mono text-gray-900 dark:text-white">
                    {edgePopupDevice.power_kw} kW
                  </dd>
                </div>
              )}
              {!edgePopupDevice.ip_address &&
                edgePopupDevice.port == null &&
                edgePopupDevice.unit_id == null &&
                !edgePopupDevice.serial_path &&
                !edgePopupDevice.modbus_profile && (
                  <p className="text-gray-500 dark:text-gray-400 text-xs">
                    {t("noEdgeData")}
                  </p>
                )}
            </dl>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Devices;